import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Megaphone, Monitor, PanelTop, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { NIVEL_CONFIG, NIVEL_COMPORTAMENTO } from "@/components/comunicados/comunicado-nivel";
import {
  ComunicadoBarraConteudo,
  ComunicadoMuralCard,
  ComunicadoSinoItem,
} from "@/components/comunicados/comunicado-superficies";
import { ComunicadoSistemaDialog } from "@/components/comunicados/ComunicadoSistemaDialog";
import { ComunicadoSistemaFormData } from "@/validations/comunicado-sistema-validation-schema";
import {
  ComunicadoSistemaNivel,
  ComunicadoSistemaVigenteDTO,
} from "@/types/comunicado-sistema.type";

/** Monta um "vigente" a partir do que está no formulário agora. */
function usePreviewDTO(): ComunicadoSistemaVigenteDTO {
  const { control } = useFormContext<ComunicadoSistemaFormData>();
  const [titulo, resumo, conteudo, nivel, inicioEm, fimEm] = useWatch({
    control,
    name: ["titulo", "resumo", "conteudo", "nivel", "inicioEm", "fimEm"],
  });

  return {
    id: -1,
    titulo: titulo?.trim() || "Título do comunicado",
    resumo:
      resumo?.trim() || "Resumo em 1–2 frases — é o que aparece na barra, no sino e nos cards.",
    conteudo: conteudo || undefined,
    nivel: (nivel as ComunicadoSistemaNivel) || "INFORMATIVO",
    inicioEm: (inicioEm ? new Date(inicioEm) : new Date()).toISOString(),
    fimEm: fimEm ? new Date(fimEm).toISOString() : undefined,
    exigeAceite: nivel === "CRITICO",
  };
}

function Superficie({
  icon,
  titulo,
  nota,
  children,
}: {
  icon: React.ReactNode;
  titulo: string;
  nota: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {icon}
        {titulo}
        <span className="font-normal normal-case tracking-normal opacity-80">— {nota}</span>
      </p>
      {children}
    </div>
  );
}

/**
 * Prévia viva: renderiza os *mesmos* componentes da barra, do sino e do mural a
 * partir do que já foi preenchido, e abre o diálogo real num clique. É o passo
 * que substitui "publicar e ver no que dá" — o admin vê as quatro superfícies
 * antes de salvar (doc §4.5).
 *
 * `compacto` mostra só a barra, para acompanhar a digitação no passo Conteúdo.
 */
export function ComunicadoPreview({ compacto }: { compacto?: boolean }) {
  const comunicado = usePreviewDTO();
  const [dialogAberto, setDialogAberto] = useState(false);
  const config = NIVEL_CONFIG[comunicado.nivel];
  const comportamento = NIVEL_COMPORTAMENTO[comunicado.nivel];

  const barra = (
    <div className={cn("flex items-center gap-2 rounded-lg border px-4 py-2", config.surface)}>
      <button
        type="button"
        onClick={() => setDialogAberto(true)}
        className="flex min-w-0 flex-1 items-center gap-2 text-left"
      >
        <ComunicadoBarraConteudo comunicado={comunicado} />
      </button>
      {comunicado.exigeAceite ? (
        <span className="shrink-0 text-[12px] font-medium underline underline-offset-2 opacity-90">
          Ver comunicado
        </span>
      ) : (
        <X className="h-4 w-4 shrink-0 opacity-70" />
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <Superficie
        icon={<PanelTop className="h-3.5 w-3.5" />}
        titulo="Barra do topo"
        nota={comportamento.barra}
      >
        {barra}
      </Superficie>

      {!compacto && (
        <>
          <Superficie
            icon={<Megaphone className="h-3.5 w-3.5" />}
            titulo="Sino do header"
            nota="lista de tudo que está no ar"
          >
            <div className="max-w-sm overflow-hidden rounded-xl border border-border/50 bg-card">
              <div className="border-b border-border/50 px-4 py-2.5 text-sm font-semibold">
                Comunicados
              </div>
              <ComunicadoSinoItem comunicado={comunicado} />
            </div>
          </Superficie>

          <Superficie
            icon={<Monitor className="h-3.5 w-3.5" />}
            titulo="Mural"
            nota="cards na Central de Módulos"
          >
            <ComunicadoMuralCard
              comunicado={comunicado}
              className="max-w-sm"
              onClick={() => setDialogAberto(true)}
            />
          </Superficie>
        </>
      )}

      <div className="flex items-center gap-3">
        <Button type="button" variant="outline" size="sm" onClick={() => setDialogAberto(true)}>
          Ver o diálogo completo
        </Button>
        <p className="text-xs text-muted-foreground">{comportamento.aceite}</p>
      </div>

      <ComunicadoSistemaDialog
        comunicado={dialogAberto ? comunicado : null}
        onOpenChange={setDialogAberto}
        preview
      />
    </div>
  );
}
